// UPDATED PROFESSIONAL VERSION - fixed sidebar, working static filters, no emojis
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { Link } from "react-router-dom";
import PartnerDisclaimerModal from "../Common/PartnerDisclaimerModal";
import PartnerNavbar from "../Partner_Navbar";

import {
  FaArrowRight,
  FaBoxOpen,
  FaChartLine,
  FaClipboardList,
  FaStar,
  FaWarehouse,
} from "react-icons/fa";

import {
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import PartnerSidebar from "../PartnerSidebar";
import "./PartnerDashboard.css";

const BASE_URL = process.env.REACT_APP_BASE_URL;

const apiClient = axios.create({
  baseURL: BASE_URL,
  withCredentials: true,
});

apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem("accessToken");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

const DISTRIBUTION_COLORS = [
  "#ad2f3b",
  "#2b2b2b",
  "#5a5a5a",
  "#8b8b8b",
  "#c8c8c8",
  "#d97a83",
  "#444444",
  "#a3a3a3",
  "#e0b4b8",
];

const formatNumber = (value) => Number(value || 0).toLocaleString("en-IN");

const formatChange = (current, previous) => {
  if (!previous) return current ? "New this period" : "No change";
  const change = ((current - previous) / previous) * 100;
  return `${change >= 0 ? "+" : ""}${change.toFixed(1)}%`;
};

const getRatingLabel = (rating) => {
  if (!rating) return "No ratings yet";
  if (rating >= 4.5) return "Excellent";
  if (rating >= 3.5) return "Good";
  if (rating >= 2.5) return "Average";
  return "Needs attention";
};

const formatOrderDate = (date) =>
  new Date(date).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });

const PartnerDashboard = () => {
  const [salesPeriod, setSalesPeriod] = useState("7");
  const [stockFilter, setStockFilter] = useState("all");
  const [orderFilter, setOrderFilter] = useState("all");
  const [topProductSort, setTopProductSort] = useState("unitsSold");
  const [dashboard, setDashboard] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    const loadDashboard = async () => {
      setIsLoading(true);

      try {
        const response = await apiClient.get("partner/dashboard", {
          params: { period: salesPeriod },
        });

        if (mounted) setDashboard(response.data?.data || null);
      } catch (error) {
        if (!mounted) return;

        toast.error(
          error.response?.data?.message || "Unable to load dashboard data",
          { position: "top-center" }
        );
      } finally {
        if (mounted) setIsLoading(false);
      }
    };

    loadDashboard();

    return () => {
      mounted = false;
    };
  }, [salesPeriod]);

  const statsData = dashboard?.stats || {};
  const currentPeriod = statsData.currentPeriod || {};
  const previousPeriod = statsData.previousPeriod || {};
  const activeProducts = dashboard?.activeProducts || 0;

  const productTypeDistribution = useMemo(
    () =>
      (dashboard?.productTypeDistribution || []).map((entry, index) => ({
        ...entry,
        color: DISTRIBUTION_COLORS[index % DISTRIBUTION_COLORS.length],
      })),
    [dashboard]
  );

  const productTypeNames = productTypeDistribution.map((entry) => entry.name);

  const stats = [
    {
      title: "Total Sales",
      value: formatNumber(statsData.totalSales),
      description: "Total units sold across all offerings",
      change: formatChange(currentPeriod.units, previousPeriod.units),
      attention: currentPeriod.units < previousPeriod.units,
      icon: <FaChartLine />,
    },
    {
      title: "Total Orders",
      value: formatNumber(statsData.totalOrders),
      description: "Orders containing your offerings",
      change: formatChange(currentPeriod.orders, previousPeriod.orders),
      attention: currentPeriod.orders < previousPeriod.orders,
      icon: <FaClipboardList />,
    },
    {
      title: "Total Products",
      value: formatNumber(statsData.totalProducts),
      description: "All listed offerings across categories",
      change: `${statsData.newProductsThisMonth || 0} new this month`,
      icon: <FaBoxOpen />,
    },
    {
      title: "Product Types",
      value: formatNumber(statsData.productTypes),
      description: "Distinct categories currently active",
      change: productTypeNames.length
        ? `${productTypeNames.slice(0, 3).join(", ")}${
            productTypeNames.length > 3 ? ` +${productTypeNames.length - 3}` : ""
          }`
        : "No active offerings",
      icon: <FaWarehouse />,
    },
    {
      title: "Average Rating",
      value: statsData.averageRating ? statsData.averageRating.toFixed(1) : "0.0",
      description: `Based on ${statsData.ratedProducts || 0} rated offerings`,
      change: getRatingLabel(statsData.averageRating),
      attention: statsData.averageRating > 0 && statsData.averageRating < 2.5,
      icon: <FaStar />,
    },
  ];

  const activeSalesTrend = dashboard?.salesTrend || [];

  const stockSummary = (dashboard?.stockSummary || []).map((stock) => ({
    ...stock,
    status: stock.label.toLowerCase().replaceAll(" ", "-"),
  }));

  const filteredStockProducts = useMemo(() => {
    const stockProducts = dashboard?.stockProducts || [];

    if (stockFilter === "all") {
      return [...stockProducts]
        .sort((a, b) => a.available - b.available)
        .slice(0, 5);
    }
    return stockProducts.filter(
      (product) =>
        product.status.toLowerCase().replaceAll(" ", "-") === stockFilter
    );
  }, [dashboard, stockFilter]);

  const filteredOrders = useMemo(() => {
    const recentOrders = dashboard?.recentOrders || [];

    if (orderFilter === "all") return recentOrders;
    return recentOrders.filter(
      (order) =>
        order.status.toLowerCase().replaceAll(" ", "-") === orderFilter
    );
  }, [dashboard, orderFilter]);

  const sortedTopProducts = dashboard?.topProducts?.[topProductSort] || [];

  const getStatusClass = (status) =>
    status.toLowerCase().replaceAll(" ", "-");

  const formatRevenue = (value) => {
    if (value >= 100000) return `₹${(value / 100000).toFixed(1)}L`;
    if (value >= 1000) return `₹${Math.round(value / 1000)}K`;
    return `₹${value}`;
  };

  const ChartTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;

    return (
      <div className="dashboard-chart-tooltip">
        <strong>{label}</strong>
        {payload.map((item) => (
          <span key={item.dataKey}>
            {item.name}: {item.dataKey === "revenue" ? formatRevenue(item.value) : item.value}
          </span>
        ))}
      </div>
    );
  };

  return (
    <>
      <PartnerNavbar />
      <div className="dashboard-layout">
        <PartnerSidebar />
  {/* <PartnerDisclaimerModal /> */}
        <main className="partner-dashboard-main">
          <header className="dashboard-header">
            <div>
              
              <h1>Dashboard</h1>
              <p>
                Monitor sales, stock, customer orders and performance of your
                artist offerings.
              </p>
            </div>

            <Link to="/SellProduct" className="dashboard-primary-button">
              <FaBoxOpen />
              Manage Products
            </Link>
          </header>

          <section className="dashboard-stat-grid">
            {stats.map((stat) => (
              <article className="dashboard-stat-card" key={stat.title}>
                <div className="stat-card-heading">
                  <span className="stat-card-icon">{stat.icon}</span>
                  <span
                    className={`stat-card-change ${
                      stat.attention ? "attention" : "positive"
                    }`}
                  >
                    {stat.change}
                  </span>
                </div>
                <span className="stat-card-title">{stat.title}</span>
                <strong className="stat-card-value">{stat.value}</strong>
                <p>{stat.description}</p>
              </article>
            ))}
          </section>

          <section className="dashboard-chart-grid">
            <article className="dashboard-card">
              <div className="dashboard-card-header">
                <div>
                  <h2>Product Distribution</h2>
                  <p>Distribution of active offerings by category</p>
                </div>
                <span className="dashboard-count-badge">{activeProducts} Active</span>
              </div>

              <div className="category-chart-content">
                <div className="category-pie-wrapper">
                  <ResponsiveContainer width="100%" height={270}>
                    <PieChart>
                      <Pie
                        data={productTypeDistribution}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        innerRadius={66}
                        outerRadius={100}
                        paddingAngle={3}
                        stroke="none"
                      >
                        {productTypeDistribution.map((entry) => (
                          <Cell key={entry.name} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => [`${value} products`, "Count"]} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="pie-chart-center">
                    <strong>{activeProducts}</strong>
                    <span>Products</span>
                  </div>
                </div>

                <div className="category-legend">
                  {productTypeDistribution.map((category) => (
                    <div className="category-legend-item" key={category.name}>
                      <span className="category-legend-name">
                        <span
                          className="category-color-dot"
                          style={{ backgroundColor: category.color }}
                        />
                        {category.name}
                      </span>
                      <strong>{category.value}</strong>
                    </div>
                  ))}
                </div>
              </div>
            </article>

            <article className="dashboard-card">
              <div className="dashboard-card-header">
                <div>
                  <h2>Sales Trend</h2>
                  <p>Orders and revenue for the selected period</p>
                </div>

                <select
                  className="dashboard-select"
                  value={salesPeriod}
                  onChange={(event) => setSalesPeriod(event.target.value)}
                  aria-label="Select sales period"
                >
                  <option value="7">Last 7 Days</option>
                  <option value="30">Last 30 Days</option>
                </select>
              </div>

              <div className="sales-chart-wrapper">
                <ResponsiveContainer width="100%" height={305}>
                  <LineChart
                    data={activeSalesTrend}
                    margin={{ top: 15, right: 12, left: -12, bottom: 0 }}
                  >
                    <CartesianGrid
                      strokeDasharray="4 4"
                      vertical={false}
                      stroke="#e3e3e3"
                    />
                    <XAxis
                      dataKey="label"
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: "#6d6d6d", fontSize: 11 }}
                    />
                    <YAxis
                      yAxisId="orders"
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: "#6d6d6d", fontSize: 11 }}
                    />
                    <YAxis
                      yAxisId="revenue"
                      orientation="right"
                      axisLine={false}
                      tickLine={false}
                      tickFormatter={formatRevenue}
                      tick={{ fill: "#6d6d6d", fontSize: 11 }}
                    />
                    <Tooltip content={<ChartTooltip />} />
                    <Legend iconType="circle" />
                    <Line
                      yAxisId="orders"
                      type="monotone"
                      dataKey="orders"
                      name="Orders"
                      stroke="#ad2f3b"
                      strokeWidth={3}
                      dot={{ r: 4, fill: "#ad2f3b", stroke: "#ffffff", strokeWidth: 2 }}
                      activeDot={{ r: 6 }}
                    />
                    <Line
                      yAxisId="revenue"
                      type="monotone"
                      dataKey="revenue"
                      name="Revenue"
                      stroke="#343434"
                      strokeWidth={3}
                      dot={{ r: 4, fill: "#343434", stroke: "#ffffff", strokeWidth: 2 }}
                      activeDot={{ r: 6 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </article>
          </section>

          <section className="dashboard-content-grid">
            <article className="dashboard-card stock-card">
              <div className="dashboard-card-header">
                <div>
                  <h2>Stock Status</h2>
                  <p>Products and offerings that may require restocking</p>
                </div>

                <select
                  className="dashboard-select"
                  value={stockFilter}
                  onChange={(event) => setStockFilter(event.target.value)}
                  aria-label="Filter stock status"
                >
                  <option value="all">All Statuses</option>
                  <option value="in-stock">In Stock</option>
                  <option value="low-stock">Low Stock</option>
                  <option value="out-of-stock">Out of Stock</option>
                </select>
              </div>

              <div className="stock-summary-grid">
                {stockSummary.map((stock) => (
                  <button
                    type="button"
                    className={`stock-summary-item ${stock.status} ${
                      stockFilter === stock.status ? "selected" : ""
                    }`}
                    key={stock.label}
                    onClick={() => setStockFilter(stock.status)}
                  >
                    <span className="stock-summary-dot" />
                    <span>
                      <strong>{stock.count}</strong>
                      <small>{stock.label}</small>
                    </span>
                  </button>
                ))}
              </div>

              <div className="dashboard-table-wrapper">
                <table className="dashboard-table">
                  <thead>
                    <tr>
                      <th>Product / Offering</th>
                      <th>Category</th>
                      <th>Available</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {!filteredStockProducts.length && (
                      <tr>
                        <td colSpan={4} className="dashboard-empty-state">
                          {isLoading ? "Loading stock..." : "No products found"}
                        </td>
                      </tr>
                    )}
                    {filteredStockProducts.map((product) => (
                      <tr key={product.id}>
                        <td>
                          <strong className="table-primary-text">
                            {product.name}
                          </strong>
                        </td>
                        <td>{product.category}</td>
                        <td>
                          {product.available} {product.unit}
                        </td>
                        <td>
                          <span
                            className={`status-tag ${getStatusClass(
                              product.status
                            )}`}
                          >
                            {product.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </article>

            <article className="dashboard-card top-products-card">
              <div className="dashboard-card-header">
                <div>
                  <h2>Top Performing Products</h2>
                  <p>Top five products by units sold or rating</p>
                </div>

                <select
                  className="dashboard-select"
                  value={topProductSort}
                  onChange={(event) => setTopProductSort(event.target.value)}
                  aria-label="Sort top products"
                >
                  <option value="unitsSold">Units Sold</option>
                  <option value="rating">Rating</option>
                </select>
              </div>

              <div className="top-products-list">
    {!sortedTopProducts.length && (
      <p className="dashboard-empty-state">
        {isLoading ? "Loading products..." : "No products listed yet"}
      </p>
    )}
    {sortedTopProducts.map((product, index) => (
      <div className="top-product-item" key={product.id}>
        <span className="top-product-rank">
          {index + 1}
        </span>

        <span className="top-product-info">
          <strong>{product.name}</strong>
          <small>{product.category}</small>
        </span>

        <span className="top-product-metric">
          <strong>
            {topProductSort === "rating"
              ? product.rating.toFixed(1)
              : product.unitsSold}
          </strong>

          <small>
            {topProductSort === "rating"
              ? "Rating"
              : "Units Sold"}
          </small>
        </span>
      </div>
    ))}
  </div>
            </article>
          </section>

          <section className="dashboard-card recent-orders-card">
            <div className="dashboard-card-header recent-orders-header">
              <div>
                <h2>Recent Orders</h2>
                <p>Latest product purchases and artist bookings</p>
              </div>

              <div className="card-header-actions">
                <select
                  className="dashboard-select"
                  value={orderFilter}
                  onChange={(event) => setOrderFilter(event.target.value)}
                  aria-label="Filter recent orders"
                >
                  <option value="all">All Statuses</option>
                  <option value="pending">Pending</option>
                  <option value="completed">Completed</option>
                </select>

                <Link to="/Partner_Orders" className="dashboard-secondary-button">
                  View All
                  <FaArrowRight />
                </Link>
              </div>
            </div>

            <div className="dashboard-table-wrapper">
              <table className="dashboard-table recent-orders-table">
                <thead>
                  <tr>
                    <th>Product Name</th>
                    <th>Quantity</th>
                    <th>Date</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {!filteredOrders.length && (
                    <tr>
                      <td colSpan={4} className="dashboard-empty-state">
                        {isLoading ? "Loading orders..." : "No orders found"}
                      </td>
                    </tr>
                  )}
                  {filteredOrders.map((order) => (
                    <tr key={order.id}>
                      <td>
                        <strong className="table-primary-text">
                          {order.product}
                        </strong>
                      </td>
                      <td>{order.quantity}</td>
                      <td>{formatOrderDate(order.date)}</td>
                      <td>
                        <span className={`status-tag ${getStatusClass(order.status)}`}>
                          {order.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </main>
      </div>
    </>
  );
};

export default PartnerDashboard;